<template>
   <v-dialog
      v-model="show"
      max-width="800"
    >
      <v-card>
        <v-card-title class="headline">
          {{ $t('import-evaluations') }}
        </v-card-title>
        <v-window v-model="step">
          <v-window-item :value="1">
            <v-card-text>
              <v-container>
                  <v-row>
                    <v-col>
                      <v-subheader>{{ $t('import-description')}}</v-subheader>
                      <v-file-input 
                        show-size
                        :label="$t('import-file')"
                        prepend-icon="mdi-file"
                        @change="initImport"
                        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      />
                    </v-col>
                  </v-row>
                  <v-row v-if="sheets.length" no-gutters>
                    <v-col>
                    <v-subheader>{{ $t('import-select-sheet')}}</v-subheader>
                    <v-radio-group v-model="selectedSheet" :mandatory="false">
                      <v-radio 
                        v-for="(sheet,index) in sheets" 
                        :key="index"
                        :label="sheet" 
                        :value="index"
                      />
                    </v-radio-group>
                    </v-col>
                  </v-row>
                  <!--
                  <v-row v-if="selectedSheet>=0" no-gutters>
                    <v-subheader>{{ $t('import-select-data-row') }}</v-subheader>
                    <v-col cols="12" sm="3">
                      <v-text-field
                        :label="$t('import-data-row-label')"
                        type="number"
                        v-model="firstRowOfData"
                      />
                    </v-col>
                  </v-row>
                  -->
              </v-container>
            </v-card-text>
          </v-window-item>
          <v-window-item :value="2">
            <v-card-text>
              <v-container :fluid="true">
                  <v-row>
                    <v-col>
                      <v-subheader>{{ $t('import-map-variables')}}</v-subheader>
                      <v-row
                        align="baseline"
                        v-for="(variable,index) in variables[selectedSheet]"
                        :key="index"
                      >
                        <v-col cols="6" align-self="baseline">
                          <p>{{ variable }}</p>
                        </v-col>
                        <v-col cols="6">
                          <v-select
                            v-model="mappings[index]"
                            :items="apifVariables"
                            :clearable="true"
                            label="Mapping"
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
              </v-container>
            </v-card-text>
          </v-window-item>
          <v-window-item :value="3">
            <v-card-text>
              <v-container>
                <v-subheader>{{ $t('import-ready') }} </v-subheader>
                <div class="scroll">
                <v-data-table
                  :headers="headers"
                  fixed-header
                  :items="mappedData"
                  :items-per-page="5"
                  class="elevation-1"
                >
                </v-data-table>
                </div>
              </v-container>
            </v-card-text>
          </v-window-item>
          <v-window-item :value="4">
            <v-card-text>
              <v-container>
                  <v-row>
                    <v-col>
                      <v-progress-linear
                        :active="isLoading"
                        :indeterminate="true"
                        height="4"
                      />
                      <v-subheader v-if="!isLoading">
                        {{ importStatus }}
                      </v-subheader>
                    </v-col>
                  </v-row>
              </v-container>
            </v-card-text>
          </v-window-item>
        </v-window>
        
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="onClose"
          >
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="onNext"
            :disabled="isNextDisabled"
          >{{ buttonLabel }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
</template>

<script>
import XLSX from 'xlsx'
import { ingredientVariables } from '@/models/Ingredient';
export default {
  props: {
    value: Boolean,
  },
  data: () => ({
    step: 1,
    maxSteps: 4,
    file: null,
    maxRows: 400,
    isLoading: false,
    sheets: [],
    selectedSheet: -1,
    firstRowOfData: 1,
    importedData: [],
    variables: [],
    rows: [],
    mappings: [],
    apifVariables: [],
    importStatus: '',
    mappedData: [],
    headers: [],
  }),
  mounted() {
    this.apifVariables = ingredientVariables.sort();
  },
  computed: {
    company() {
      return this.$store.getters["company/getCompany"];
    },
    activeProject() {
      return this.$store.getters["projects/getActiveProject"];
    },
    isNextDisabled() {
      if (this.step === 1 && this.selectedSheet < 0) {
        return true;
      } else {
        return false;
      }
    },
    buttonLabel() {
      let label = this.$t('next');
      if (this.step == 3) {
        label = this.$t('import');
      }
      if (this.step == 4) {
        label = this.$t('finish');
      }
      return label;
    },
    show: {
      get () {
        return this.value
      },
      set (value) {
         this.$emit('input', value)
      }
    },
  },
  methods: {
    onClose() {
      this.show = false;
    },
    onNext() {
      if (this.step === 1) {
        if(!this.checkMaxRows()) {
          return;
        }
      }
      if (this.step === 2) {
        this.mapVariables();
      }
      if (this.step === 3) {
        this.onImport();
      }

      if (this.step<this.maxSteps) {
        this.step++;
      }
      else {
        this.onClose();
      }      
    },
    initImport() {
      if (!this.activeProject.id) {
        this.$notify({
          group: "base",
          type: "error",
          title: "No active project selected",
          text: "Select a project before creating an ingredient.",
        });
        return;
      }

      const input = event.target;
      const reader = new FileReader();
      reader.onload = () => {
        const fileData = reader.result;
        const wb = XLSX.read(fileData, {type : 'binary'});

        //const fromRow = this.firstRowOfData - 1;

        this.sheets = wb.SheetNames;

        wb.SheetNames.forEach((sheetName) => {
          const worksheet = wb.Sheets[sheetName]
          const arr = XLSX.utils.sheet_to_json(worksheet, { header: 1});

          this.importedData.push(arr);
          this.variables.push(arr[0]);
          this.rows.push(arr.slice(1))
        })
      };
      reader.readAsBinaryString(input.files[0]);

    },
    checkMaxRows() {
      if(this.rows[this.selectedSheet].length > this.maxRows) {
        this.$notify({
          group: "base",
          type: "error",
          title: "Too many rows for import",
          text: "Unfortunately you can only import a maximum of " + this.maxRows + " rows at a time",
        });
        return false;
      } else {
        return true;
      } 
    },
    mapVariables() {
      const rows = this.rows[this.selectedSheet];

      this.mappedData = rows.map((row) => {
          for(let j = 0; j < row.length; j++) {
            if(typeof this.mappings[j] != 'undefined') {
              row[this.mappings[j]] = row[j];
            }
            delete row[j];
            row['projectId'] = this.activeProject.id;
            row['companyId'] = this.company.id;
          }
          return {...row};
      });

      // fix headers
      this.headers = this.mappings.map((item) => {
        return {
          text: item,
          value: item,
        }
      });
    },
    async onImport() {

      this.isLoading = true;

      if (!this.activeProject.id) {
        this.importStatus = 'Could not find an active project, please refresh the app and try again.';
        return false;
      }
    
      await this.$store.dispatch('projectIngredients/importProjectIngredients', {projectId: this.activeProject.id, data: this.mappedData})
        .then(() => {
          this.importStatus = 'Import completed succesfully: ' + this.mappedData.length + ' items has been imported',
          this.isLoading = false;
        })
        .catch((err) => {
          this.importStatus = 'Yikes, found a error. You should probably contact support with the following information: '+err,
          this.isLoading = false;
        })
    },
  }
}
</script>

<style scoped>
.scroll {
  overflow: auto;
  height: 500px;
}
</style>